import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Plus } from "lucide-react";
import { Badge } from "@/components/atoms/Badge";
import { Button } from "@/components/atoms/Button";
import { Tooltip } from "@/components/atoms/Tooltip";
import { FilterBar, FilterBarLabel, FilterScope } from "@/components/molecules/FilterBar";
import { ScopePicker } from "@/components/molecules/ScopePicker";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { MultiSelect } from "@/components/molecules/MultiSelect";
import { PageHeader } from "@/components/molecules/PageHeader";
import { Pagination } from "@/components/molecules/Pagination";
import { RefreshButton } from "@/components/molecules/RefreshButton";
import { SavedFiltersButton } from "@/components/molecules/SavedFiltersButton";
import { SearchInput } from "@/components/molecules/SearchInput";
import { DataTable, type Column } from "@/components/organisms/DataTable";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { usePagedResource } from "@/hooks/usePagedResource";
import { useScopeOptions } from "@/hooks/useScopeOptions";
import { useScopeLabels } from "@/hooks/useScopeLabels";
import { scopeParams } from "@/lib/scopeParams";
import { usePageScope, useSettingsStore } from "@/store/settingsStore";
import { campaignsService } from "@/services/campaignsService";
import { propertiesService } from "@/services/propertiesService";
import { whatsappTemplateService } from "@/services/whatsappTemplateService";
import { propertyDisplayLabel } from "@/lib/propertyDisplay";
import { useUiStore } from "@/store/uiStore";
import { getApiErrorMessage } from "@/utils/apiErrors";
import { formatDateAbsolute } from "@/utils/date-helpers";
import type { PageRequest } from "@/types/api";
import type { Campaign, CampaignFilter } from "@/types/campaign";
import { CampaignDetailDrawer } from "./CampaignDetailDrawer";
import { CampaignFormDrawer } from "./CampaignFormDrawer";
import { CampaignLabelCell } from "./CampaignLabelCell";
import {
  CAMPAIGN_STATUS_TONE,
  RUN_STATUS_TONE,
  displayRunStatus,
  SEND_MODE_OPTIONS,
  STATUS_OPTIONS,
  nextRunLabel,
  scheduleSummary,
  sendModeLabel,
  targetTypeLabel,
} from "./campaignConstants";

/** Distinct, non-blank ids from a column of loosely-typed row values. */
function uniqueIds(values: (string | number | null | undefined)[]): string[] {
  const seen = new Set<string>();
  for (const value of values) {
    if (value == null) continue;
    const id = String(value).trim();
    if (id) seen.add(id);
  }
  return [...seen].sort();
}

/**
 * Tooltip for a cell that shows a resolved name in place of an id: the name on
 * its own. Empty while the lookup is in flight or when it came back without a
 * name for the id, in which case the cell already shows the raw id.
 */
function nameTooltip(
  value: string | number | null | undefined,
  names: Record<string, string>,
): string {
  const id = value != null ? String(value).trim() : "";
  if (!id) return "";
  return names[id] ?? "";
}

function DeliveryCell({ campaign }: { campaign: Campaign }) {
  const summary = campaign.delivery_summary;
  if (!summary) return <span className="text-bridge-subtle">Not run yet</span>;
  const sent = summary.sent_count ?? 0;
  const targeted = summary.targeted_count ?? 0;
  const failed = summary.failed_count ?? 0;
  const status = displayRunStatus(summary);
  return (
    <div className="leading-tight">
      <Badge tone={RUN_STATUS_TONE[status] ?? "neutral"} className="h-5">
        {status || "—"}
      </Badge>
      <div className="mt-0.5 text-xs text-bridge-muted tabular-nums">
        {sent}/{targeted} sent{failed > 0 ? ` · ${failed} failed` : ""}
      </div>
    </div>
  );
}

export function CampaignsPage() {
  const { startPageLoading, stopPageLoading } = useUiStore();

  const [detailId, setDetailId] = useState<number | null>(null);
  const [detailRow, setDetailRow] = useState<Campaign | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);

  const scope = usePageScope("campaigns");
  const [labelReloadToken, setLabelReloadToken] = useState(0);
  const { entities } = useScopeOptions();
  // Clear filters spans the scope picker too: it registers with the same FilterScope,
  // so it counts towards the badge and has to be reset by the same control.
  const clearScope = useSettingsStore((s) => s.clearScope);
  // The campaign service speaks snake_case, so the shared scope params are renamed on the
  // way out rather than sent under the Atlas spellings it would ignore.
  // An empty picker ("All legal entities") still sends every entity in the dropdown —
  // omitting legal_entity_id returns nothing.
  const scopeQuery = useMemo(() => {
    const { legalEntityIds, branchIds } = scopeParams(scope);
    const allLegalEntityIds = entities.map((e) => e.id).filter(Boolean).join(",");
    const legalEntityId = legalEntityIds || (!branchIds ? allLegalEntityIds : undefined);
    return {
      ...(legalEntityId ? { legal_entity_id: legalEntityId } : {}),
      ...(branchIds ? { branch_ids: branchIds } : {}),
    };
  }, [scope, entities]);

  const fetcher = useCallback(
    (filter: CampaignFilter, page: PageRequest) =>
      campaignsService.list({ ...filter, ...scopeQuery }, page),
    [scopeQuery],
  );

  const list = usePagedResource<Campaign, CampaignFilter>(fetcher, {
    initialPage: { page: 0, size: 20 },
  });

  useEffect(() => {
    if (!list.error) return;
    toast.error(getApiErrorMessage(list.error) ?? "Failed to load campaigns. Please try again.", {
      toastId: "campaigns-list-error",
    });
  }, [list.error]);

  const [search, setSearch] = useState(list.filter.search ?? "");
  const debouncedSearch = useDebouncedValue(search, 300);

  useEffect(() => {
    if ((list.filter.search ?? "") === debouncedSearch) return;
    list.setFilter({ search: debouncedSearch || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  /**
   * Labels for the filter come from Atlas (`/api/ui/v1/labels`), not the campaign
   * service, and follow the scope picker: changing the legal entity or branch
   * above re-lists them. The campaign rows' own labels aren't enough — they only
   * cover the labels used on the page currently loaded.
   */
  const { labels, loading: loadingLabels } = useScopeLabels("campaigns", {
    reloadToken: labelReloadToken,
  });

  const labelOptions = useMemo(
    () =>
      labels.map((label) => ({
        value: String(label.id),
        label: label.name?.trim() || String(label.id),
        subtitle: label.labelDescription?.trim() || undefined,
      })),
    [labels],
  );

  /** `label_ids` travels comma-separated; the picker works in ids. */
  const selectedLabelIds = useMemo(
    () => (list.filter.label_ids ?? "").split(",").map((id) => id.trim()).filter(Boolean),
    [list.filter.label_ids],
  );

  /**
   * Campaign rows only carry template/property ids, so the names are resolved in
   * a second pass once a page has loaded: the ids on screen are batched into one
   * POST per kind, both in flight together. Unresolved ids keep showing as ids
   * rather than blanking the cell.
   */
  const [propertyNames, setPropertyNames] = useState<Record<string, string>>({});
  const [templateNames, setTemplateNames] = useState<Record<string, string>>({});

  // Keyed on the loaded page itself rather than on the ids it holds: every
  // campaigns fetch — first load, paging, a filter change and Refresh — hands
  // back a new array, so a refresh re-resolves the names even when the same ids
  // come back, while a plain re-render (which reuses the array) does not.
  const rows = list.data?.content;
  /**
   * The page the name lookups have finished for. Comparing it to the current
   * page tells us synchronously — in the same render the campaigns arrive, before
   * the lookup effect has run — that names are still outstanding, so the page
   * loader below stays up across all three calls instead of blinking off in
   * between the campaigns response and the lookups it triggers.
   */
  const [resolvedRows, setResolvedRows] = useState<Campaign[] | undefined>(undefined);

  useEffect(() => {
    const propertyIds = uniqueIds((rows ?? []).map((r) => r.property_id));
    const templateIds = uniqueIds((rows ?? []).map((r) => r.template_id));
    if (propertyIds.length === 0 && templateIds.length === 0) {
      // Nothing to look up — this page counts as resolved, or the loader would
      // never come down for a list with no property or template ids on it.
      setResolvedRows(rows);
      return;
    }

    let cancelled = false;
    // Independent lookups — one failing must not cost the other its names.
    void Promise.allSettled([
      propertiesService.listByIds(propertyIds),
      whatsappTemplateService.listByIds(templateIds),
    ]).then(([properties, templates]) => {
      if (cancelled) return;
      if (properties.status === "fulfilled") {
        setPropertyNames((prev) => {
          const next = { ...prev };
          for (const p of properties.value) {
            if (p?.id) next[String(p.id)] = propertyDisplayLabel(p);
          }
          return next;
        });
      }
      if (templates.status === "fulfilled") {
        setTemplateNames((prev) => {
          const next = { ...prev };
          for (const t of templates.value) {
            const name = t?.templateName?.trim();
            if (t?.id && name) next[String(t.id)] = name;
          }
          return next;
        });
      }
      setResolvedRows(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [rows]);

  /**
   * Page loader spans the whole sequence: the campaigns request and the two name
   * lookups that follow it. `rows === undefined` is the very first load, where
   * `list.loading` already covers it.
   */
  const resolvingNames = rows !== undefined && rows !== resolvedRows;

  useEffect(() => {
    if (list.loading || resolvingNames) startPageLoading();
    else stopPageLoading();
    return () => stopPageLoading();
  }, [list.loading, resolvingNames, startPageLoading, stopPageLoading]);

  const handleRowClick = (campaign: Campaign) => {
    setDetailRow(campaign);
    setDetailId(campaign.id);
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetailRow(null);
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  // Editing is reached from the detail drawer — close it so the two drawers
  // don't stack, and come back to a refreshed list once the save lands.
  const openEdit = (campaign: Campaign) => {
    closeDetail();
    setEditing(campaign);
    setFormOpen(true);
  };

  const handleSaved = () => {
    list.reloadFirstPage();
  };

  const columns = useMemo<Column<Campaign>[]>(
    () => [
      {
        id: "name",
        header: "Campaign",
        width: "w-[280px]",
        cellTooltip: (r) => r.name ?? "",
        cell: (r) => (
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold leading-tight text-slate-800">{r.name || "(unnamed)"}</div>
            <div className="truncate text-xs leading-tight text-bridge-muted">
              {targetTypeLabel(r.target_type)} · {sendModeLabel(r.send_mode)}
            </div>
          </div>
        ),
      },
      {
        id: "status",
        header: "Status",
        width: "w-[120px]",
        cell: (r) =>
          r.status ? (
            <Badge tone={CAMPAIGN_STATUS_TONE[String(r.status)] ?? "neutral"} className="h-5">{r.status}</Badge>
          ) : (
            <span className="text-bridge-subtle">—</span>
          ),
      },
      {
        id: "labels",
        header: "Label",
        width: "w-[220px]",
        align: "center",
        className: "overflow-hidden",
        cell: (r) => <CampaignLabelCell labels={r.labels} />,
      },
      {
        id: "schedule",
        header: "Schedule",
        width: "w-[190px]",
        cell: (r) => <span className="text-bridge-fg">{scheduleSummary(r)}</span>,
      },
      {
        id: "next_run_at",
        header: "Next Run",
        width: "w-[170px]",
        cell: (r) => <span className="text-bridge-muted">{nextRunLabel(r)}</span>,
      },
      {
        id: "delivery_summary",
        header: "Last Delivery",
        width: "w-[190px]",
        cell: (r) => <DeliveryCell campaign={r} />,
      },
      {
        id: "template_id",
        header: "Template",
        width: "w-[260px]",
        // The full name, since the cell truncates it.
        cellTooltip: (r) => nameTooltip(r.template_id, templateNames),
        cell: (r) => {
          const id = r.template_id != null ? String(r.template_id).trim() : "";
          if (!id) return "—";
          return <span className="truncate">{templateNames[id] ?? id}</span>;
        },
      },
      {
        id: "property_id",
        header: "Property",
        width: "w-[260px]",
        // Older campaigns predate the property link and campaigns that fill no
        // property placeholder never carry one, so an absent id is the norm
        // rather than an error — shown as a dash like the other optional cells.
        cellTooltip: (r) => nameTooltip(r.property_id, propertyNames),
        cell: (r) => {
          const propertyId = r.property_id != null ? String(r.property_id).trim() : "";
          if (!propertyId) return "—";
          return <span className="truncate">{propertyNames[propertyId] ?? propertyId}</span>;
        },
      },
      {
        id: "updated_at",
        header: "Updated",
        width: "w-[170px]",
        cell: (r) => <span className="text-bridge-muted">{formatDateAbsolute(r.updated_at)}</span>,
      },
    ],
    [propertyNames, templateNames],
  );

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Campaigns"
        subtitle={`${list.data?.totalElements ?? 0} outbound campaigns targeting your labelled audiences.`}
        actions={
          <>
            <SavedFiltersButton
              pageName="campaigns"
              scopePage="campaigns"
              captureFilterState={() => ({
                search,
                label_ids: list.filter.label_ids,
                status: list.filter.status,
                send_mode: list.filter.send_mode,
              })}
              applyFilterState={(state) => {
                const restoredSearch = typeof state.search === "string" ? state.search : "";
                setSearch(restoredSearch);
                list.setFilter({
                  search: restoredSearch || undefined,
                  label_ids: typeof state.label_ids === "string" ? state.label_ids : undefined,
                  status: typeof state.status === "string" ? state.status : undefined,
                  send_mode: typeof state.send_mode === "string" ? state.send_mode : undefined,
                });
              }}
            />
            <RefreshButton onClick={list.reload} loading={list.loading} />
            <Button variant="primary" onClick={openCreate}>
              <Plus size={16} />
              New Campaign
            </Button>
          </>
        }
      />

      <FilterScope
        onClear={() => {
          setSearch("");
          clearScope("campaigns");
          list.clearFilter();
        }}
      >
        <div className="glass mx-6 mb-6 flex flex-1 flex-col overflow-hidden rounded-lg">
          <FilterBar className="bg-bridge-background">
            <FilterBarLabel className="min-w-[80px]" />
            <ScopePicker
              page="campaigns"
              expandAllEntities
              onApply={() => {
                setLabelReloadToken((n) => n + 1);
                list.setFilter({ label_ids: undefined });
              }}
            />
            <Tooltip side="top" hoverOnly content="Show campaigns targeting any of the labels you tick.">
              <div className="min-w-[180px] max-w-[240px]">
                <MultiSelect
                  hideLabel
                  enableSearch
                  value={selectedLabelIds}
                  onChange={(ids) => list.setFilter({ label_ids: ids.length > 0 ? ids.join(",") : undefined })}
                  options={labelOptions}
                  placeholder="Any label"
                  loading={loadingLabels}
                  searchPlaceholder="Search labels..."
                  emptyText="No labels in this account"
                />
              </div>
            </Tooltip>
            <Tooltip side="top" hoverOnly content="Search campaigns by name.">
              <div>
                <SearchInput value={search} onChange={setSearch} placeholder="Search campaigns..." />
              </div>
            </Tooltip>
            <Tooltip side="top" hoverOnly content="Show campaigns in a given lifecycle state.">
              <div className="min-w-[160px] max-w-[200px]">
                <MenuSelect
                  value={list.filter.status ?? ""}
                  onChange={(v) => list.setFilter({ status: v || undefined })}
                  options={[{ value: "", label: "Any status" }, ...STATUS_OPTIONS]}
                  placeholder="Any status"
                />
              </div>
            </Tooltip>
            <Tooltip side="top" hoverOnly content="Show one-off, immediate or recurring campaigns.">
              <div className="min-w-[160px] max-w-[200px]">
                <MenuSelect
                  value={list.filter.send_mode ?? ""}
                  onChange={(v) => list.setFilter({ send_mode: v || undefined })}
                  options={[{ value: "", label: "Any send mode" }, ...SEND_MODE_OPTIONS]}
                  placeholder="Any send mode"
                />
              </div>
            </Tooltip>
          </FilterBar>

          <div className="scrollbar-metal flex-1 overflow-auto">
            <DataTable
              rows={list.data?.content ?? []}
              columns={columns}
              loading={list.loading}
              rowKey={(r) => String(r.id)}
              onRowClick={handleRowClick}
              markVisitedRow
              dense
              customization={{
                tableId: "campaigns",
                lockedColumnIds: ["name"],
                pinnedStartColumnIds: ["name"],
              }}
              emptyTitle="No campaigns match"
              emptyDescription="Create a campaign to start reaching a labelled audience."
            />
          </div>

          <Pagination
            page={list.data?.page ?? 0}
            totalPages={list.data?.totalPages ?? 0}
            totalElements={list.data?.totalElements ?? 0}
            size={list.data?.size ?? 30}
            onPageChange={list.setPage}
          />
        </div>
      </FilterScope>

      <CampaignDetailDrawer
        open={detailId != null}
        campaignId={detailId}
        fallback={detailRow}
        templateNames={templateNames}
        propertyNames={propertyNames}
        onClose={closeDetail}
        onEdit={openEdit}
        onStatusChange={() => list.reloadFirstPage()}
      />

      <CampaignFormDrawer
        open={formOpen}
        campaign={editing}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSaved={handleSaved}
      />
    </div>
  );
}
